import React from 'react';

export interface IOrbitMarkProps {
  /** Drawn slot width. 24 in the open panel's header (`5017:9836`). */
  width?: number | string;
  /** Drawn slot height. 25 in the open panel's header (`5017:9836`). */
  height?: number | string;
  className?: string;
  /**
   * Ink for all five vectors. A literal, not a `var()` reference: an SVG `fill` attribute is not a
   * CSS declaration and does not substitute custom properties.
   */
  fill?: string;
}

/**
 * The Orbit mark as the designer exported it, BRAND TERRACOTTA: Figma `5118:16828`, five vectors in
 * a 24 x 25 box. This is the mark the panel wears while the chat is OPEN - `figma-spec-v3.md` draws
 * it at `5017:9836` "Orbit Icon 5", 24 x 25 in `op-head`, with all five vectors filled `#D2704C`
 * (`5017:9837`-`5017:9841`).
 *
 * The fill defaults to the drawn `#D2704C`, as the export carries it; `perx_3_redesign` passes the
 * light palette's `mark` instead.
 * `OrbitMarkWhite` is the sibling white mark for the launcher tile, `OrbitMarkReply` the 14 x 15
 * terracotta one beside each assistant reply. The
 * exported `clipPath` is dropped - its rect is the whole 24 x 25 viewBox, so it clipped nothing and
 * only risked a duplicate element id wherever two marks render at once.
 */
const OrbitMark: React.FC<IOrbitMarkProps> = ({
  width = 24,
  height = 25,
  className,
  fill = '#D2704C'
}) => (
  <svg
    className={className}
    width={width}
    height={height}
    viewBox="0 0 24 25"
    preserveAspectRatio="xMidYMid meet"
    fill="none"
    focusable="false"
    aria-hidden="true"
    data-testid="orbit-mark"
  >
    <path
      d="M19.846 0C20.7471 1.34347 22.5713 1.7114 23.9287 0.82691L23.9446 0.816442C22.5856 1.71427 22.2173 3.53547 23.122 4.8842C22.2173 3.53547 20.3823 3.16993 19.0234 4.06776C20.3823 3.16993 20.7506 1.34873 19.846 0Z"
      fill={fill}
    />
    <path
      d="M22.6994 2.0625C15.7379 5.83171 5.3073 12.772 3.77206 18.009C3.48365 18.9928 3.12899 20.0434 3.12898 21.0686C3.12898 22.567 4.39756 23.7818 5.9625 23.7818C6.41138 23.7818 6.83588 23.6818 7.21305 23.5039C7.39431 23.4184 7.5994 23.386 7.79195 23.4416C8.46008 23.6346 8.46453 24.5105 7.78363 24.6518C5.56095 25.1131 2.73094 25.2935 1.22896 24.0651C-1.36307 21.9451 0.760183 11.7898 22.6994 2.0625Z"
      fill={fill}
    />
    <path
      d="M17.2762 3.15003C17.6979 2.53595 17.5439 1.68243 16.8768 1.35104C15.3365 0.585955 13.877 0.429531 11.9223 0.442085C9.48503 0.45774 7.1103 1.20963 5.11415 2.59771C3.11799 3.98579 1.59506 5.94425 0.747994 8.21248C0.116997 9.90213 -0.116671 11.704 0.0541961 13.4822C0.14268 14.403 1.16602 14.8351 1.96638 14.3712C2.50784 14.0574 2.75679 13.4232 2.67819 12.8023C2.51818 11.5383 2.75555 10.542 3.27798 9.14312C3.93459 7.3849 5.11509 5.8668 6.66242 4.79083C8.20974 3.71485 10.0505 3.13202 11.9398 3.11989C13.3327 3.11094 14.3137 3.15474 15.3587 3.60156C16.0368 3.89154 16.8587 3.75803 17.2762 3.15003Z"
      fill={fill}
    />
    <path
      d="M19.5413 4.86988C20.0653 4.3498 20.9177 4.34549 21.3804 4.92074C22.1647 5.89555 22.7915 6.98881 23.2356 8.16161C23.8372 9.75023 24.0898 11.4481 23.9764 13.1415C23.863 14.8349 23.3862 16.4847 22.578 17.9803C21.7699 19.4759 20.649 20.7829 19.2904 21.8135C17.9319 22.8442 16.3671 23.5748 14.7008 23.9564C13.0346 24.338 11.3055 24.3617 9.6293 24.026C7.95309 23.6903 6.36851 23.003 4.98176 22.01C3.96426 21.2815 3.07049 20.4007 2.33109 19.4015C1.88667 18.801 2.11739 17.9681 2.76698 17.599C3.41061 17.2333 4.22153 17.4635 4.68016 18.0446C5.21756 18.7254 5.85009 19.3297 6.56043 19.8384C7.63537 20.6081 8.86366 21.1409 10.1629 21.4011C11.4623 21.6613 12.8026 21.6429 14.0942 21.3471C15.3858 21.0513 16.5987 20.485 17.6518 19.686C18.7049 18.8871 19.5738 17.8741 20.2002 16.7147C20.8267 15.5554 21.1962 14.2765 21.2841 12.9639C21.372 11.6513 21.1763 10.3352 20.7099 9.10373C20.4036 8.29475 19.9851 7.53459 19.4683 6.8445C19.0209 6.24721 19.0117 5.39554 19.5413 4.86988Z"
      fill={fill}
    />
    <path
      d="M3.95268 12.931C5.67496 11.2703 4.63685 9.52854 2.57902 10.749L1.39401 11.3896C0.537725 11.8526 -0.036535 12.739 0.0471438 13.7088C0.0554083 13.8046 0.0647172 13.8973 0.0749677 13.9871C0.227201 15.3204 1.53517 15.4084 2.45886 14.4349C2.94443 13.9231 3.44445 13.421 3.95268 12.931Z"
      fill={fill}
    />
  </svg>
);

export default OrbitMark;
